/**
 * Turns a parseSearch() result into the Search Page it should open: room tag, market and query params.
 * Pure, so the location rules can be tested without a page.
 */

export const MARKET_SUBDIRS = {
  GB: 'uk',
  US: 'us',
  AU: 'au',
  CA: 'ca',
  IE: 'ie',
  SG: 'sg',
};

/** The Search Page's "all event spaces" tag – what the control form searches when no event type is picked. */
const PLAIN_ROOM_TAG = 'event-venue';

/**
 * @typedef {object} SearchLocation
 * @property {string} subdir market of the Search Page, e.g. "uk"
 * @property {Array<[string, string]>} params location query params, e.g. [["location_id", "6"]]
 */

/**
 * @typedef {object} SearchPage
 * @property {string} country ISO code of the market the page belongs to
 * @property {?string} cityKey vocab key of the city the text is read against, e.g. "London|GB"
 * @property {SearchLocation} location where to search when the text names no place of its own
 * @property {boolean} followsOtherCity whether a city named in the text replaces that location
 */

/**
 * Vocab key ("Name|CC") for a city the parser named, preferring the page's own market: the parser keys its city
 * list by name only, so "Newcastle" alone could be either the GB or the AU one.
 *
 * @returns {?string}
 */
export const findCityKey = (cityName, countryCode, vocab) => {
  const sameMarket = `${cityName}|${countryCode}`;
  if (vocab.locations[sameMarket]) {
    return sameMarket;
  }

  return Object.keys(vocab.locations).find((key) => vocab.locations[key].city === cityName) ?? null;
};

/**
 * @returns {?string} vocab key of the city with this Location Tree id
 */
export const findCityKeyByLocationId = (locationId, locationIds) => {
  const id = Number(locationId);

  return Object.keys(locationIds).find((key) => locationIds[key].id === id) ?? null;
};

/** Shorter area names ("Bow", "Bank") are ordinary words too often to fill a Location on their own. */
const MIN_AREA_NAME_LENGTH = 4;

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * City of the first area of the market named in the text, for a Home page whose Location field is still empty.
 * Area names repeat across cities ("West End"), so the market's default city is checked first.
 *
 * @param {?string} preferredCityKey
 * @returns {?string} vocab key of that city
 */
export const findCityKeyByAreaInText = (text, countryCode, vocab, preferredCityKey = null) => {
  const haystack = ` ${text.toLowerCase().replace(/['’]/g, '')} `;
  const marketKeys = Object.keys(vocab.locations).filter((key) => key.endsWith(`|${countryCode}`));
  const cityKeys = preferredCityKey && marketKeys.includes(preferredCityKey)
    ? [preferredCityKey, ...marketKeys.filter((key) => key !== preferredCityKey)]
    : marketKeys;

  return cityKeys.find((key) => vocab.locations[key].areas.some((area) => {
    const name = area.toLowerCase().replace(/['’]/g, '');

    return name.length >= MIN_AREA_NAME_LENGTH
      && haystack.includes(name)
      && new RegExp(`[^a-z0-9]${escapeRegExp(name)}[^a-z0-9]`).test(haystack);
  })) ?? null;
};

/**
 * @returns {SearchLocation}
 */
const resolveLocation = ({ area, other_city: otherCity }, page, vocab, locationIds) => {
  const areaId = area && page.cityKey ? locationIds[page.cityKey]?.areas[area] : null;
  if (areaId) {
    return { subdir: page.location.subdir, params: [['location_id', String(areaId)]] };
  }

  if (otherCity && page.followsOtherCity) {
    const cityKey = findCityKey(otherCity, page.country, vocab);
    const cityId = cityKey ? locationIds[cityKey]?.id : null;

    if (cityId) {
      const [, countryCode] = cityKey.split('|');

      return { subdir: MARKET_SUBDIRS[countryCode] ?? page.location.subdir, params: [['location_id', String(cityId)]] };
    }
  }

  return page.location;
};

/**
 * Support questions ("refund on my booking"), gibberish ("KM") and an empty box all run the page's plain search,
 * with no filters.
 *
 * @param {?object} result parseSearch() output, null when there is nothing to parse
 * @param {SearchPage} page
 * @returns {{ roomTag: string, subdir: string, params: Array<[string, string]> }}
 */
export const resolveSearch = (result, page, vocab, locationIds) => {
  if (!result || result.intent !== 'venue_search' || !result.search) {
    return { roomTag: PLAIN_ROOM_TAG, ...page.location };
  }

  const location = resolveLocation(result.location, page, vocab, locationIds);

  return {
    roomTag: result.search.room_tag,
    subdir: location.subdir,
    params: [...location.params, ...result.search.params],
  };
};
